// Universal export utility for Gao Chill 五告凉 reports.
// Produces branded PDF (jspdf + autotable), CSV (papaparse), and XLSX (exceljs).
// All exports run purely in the browser — no server dependencies.

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Papa from "papaparse";
import ExcelJS from "exceljs";
import { formatCurrency } from "./formatters";

const BRAND = {
  name: "Gao Chill 五告凉",
  namePDF: "Gao Chill",     // jsPDF default helvetica is Latin-1 only; CJK is embedded as needed via CSV/XLSX instead.
  primary: [25, 63, 125],   // #193f7d
  accent:  [250, 157, 28],  // #fa9d1c
};

// ---------- helpers ----------
const nowStamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fileSafe = (s) => (s || "report").toString().replace(/[^A-Za-z0-9-_.]+/g, "-");

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ---------- PDF ----------
/**
 * Generate a professional A4 PDF report.
 * @param {object} opts
 *   title       - report title
 *   subtitle    - selected period, e.g. "July 2026"
 *   filename    - output filename without extension
 *   sections    - [{ heading, columns:[{key,label,align,formatter?}], rows:[obj], totals?: obj }]
 *   metrics     - [{ label, value }] shown above table (optional)
 *   footnote    - optional footer text
 */
export function exportPDF({ title, subtitle, filename, sections = [], metrics = [], footnote }) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 14;

  // --- Header block ---
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, pageWidth, 24, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(BRAND.namePDF, marginX, 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Business Report — MYR", marginX, 16);
  doc.text(`Generated ${nowStamp()}`, pageWidth - marginX, 16, { align: "right" });

  // --- Title ---
  doc.setTextColor(25, 63, 125);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(title, marginX, 34);
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(90, 90, 90);
    doc.text(subtitle, marginX, 40);
  }

  let cursorY = 46;

  // --- Metric strip ---
  if (metrics.length) {
    const boxW = (pageWidth - marginX * 2 - (metrics.length - 1) * 4) / metrics.length;
    metrics.forEach((m, i) => {
      const x = marginX + i * (boxW + 4);
      doc.setFillColor(240, 244, 250);
      doc.rect(x, cursorY, boxW, 16, "F");
      doc.setTextColor(120, 120, 130);
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.text(m.label.toUpperCase(), x + 3, cursorY + 5);
      doc.setTextColor(20, 20, 30);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(String(m.value), x + 3, cursorY + 12);
    });
    cursorY += 22;
  }

  // --- Sections ---
  sections.forEach((sec) => {
    if (sec.heading) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(25, 63, 125);
      doc.text(sec.heading, marginX, cursorY);
      cursorY += 5;
    }
    if (sec.columns && sec.columns.length) {
      const headRow = sec.columns.map((c) => c.label);
      const bodyRows = (sec.rows || []).map((r) =>
        sec.columns.map((c) => {
          const raw = r[c.key];
          if (c.formatter) return c.formatter(raw, r);
          if (raw == null) return "";
          if (c.type === "currency") return formatCurrency(Number(raw || 0));
          return String(raw);
        })
      );
      // Optional totals row (bold)
      const foot = sec.totals ? [sec.columns.map((c) => {
        const raw = sec.totals[c.key];
        if (raw == null) return "";
        if (c.formatter) return c.formatter(raw, sec.totals);
        if (c.type === "currency") return formatCurrency(Number(raw || 0));
        return String(raw);
      })] : undefined;
      autoTable(doc, {
        head: [headRow],
        body: bodyRows,
        foot,
        startY: cursorY,
        margin: { left: marginX, right: marginX },
        theme: "grid",
        styles: { font: "helvetica", fontSize: 9, cellPadding: 2, overflow: "linebreak" },
        headStyles: { fillColor: BRAND.primary, textColor: 255, fontStyle: "bold", fontSize: 9 },
        footStyles: { fillColor: [240, 244, 250], textColor: [20, 20, 30], fontStyle: "bold" },
        columnStyles: sec.columns.reduce((acc, c, i) => {
          const align = c.align || (c.type === "currency" ? "right" : "left");
          acc[i] = { halign: align, cellWidth: c.width };
          return acc;
        }, {}),
        didDrawPage: () => {
          const pn = doc.internal.getCurrentPageInfo().pageNumber;
          const total = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(140, 140, 150);
          doc.text(`Page ${pn} / ${total}`, pageWidth - marginX, doc.internal.pageSize.getHeight() - 6, { align: "right" });
        },
      });
      cursorY = doc.lastAutoTable.finalY + 8;
    }
    // Section-level summary text
    if (sec.summary) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 70);
      const lines = doc.splitTextToSize(sec.summary, pageWidth - marginX * 2);
      lines.forEach((ln) => { doc.text(ln, marginX, cursorY); cursorY += 4; });
      cursorY += 4;
    }
  });

  if (footnote) {
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 150);
    // Replace any non-Latin-1 characters in the footnote so jsPDF's default
    // font doesn't render them as glyph noise.
    const safeFootnote = footnote.replace(/[^\x00-\xFF]/g, "");
    const lines = doc.splitTextToSize(safeFootnote, pageWidth - marginX * 2);
    doc.text(lines, marginX, cursorY + 6);
  }

  doc.save(`${fileSafe(filename)}.pdf`);
}

// ---------- CSV ----------
export function exportCSV({ columns, rows, filename }) {
  const header = columns.map((c) => c.label);
  const data = rows.map((r) =>
    columns.map((c) => {
      const v = r[c.key];
      if (c.formatter) return c.formatter(v, r);
      if (v == null) return "";
      // For CSV keep numbers as numbers so Excel opens them numerically.
      if (c.type === "currency") return Number(v || 0).toFixed(2);
      return v;
    })
  );
  const csv = Papa.unparse({ fields: header, data });
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, `${fileSafe(filename)}.csv`);
}

// ---------- XLSX ----------
export async function exportXLSX({ title, subtitle, columns, rows, filename, totals }) {
  const wb = new ExcelJS.Workbook();
  wb.creator = BRAND.name;
  wb.created = new Date();
  const ws = wb.addWorksheet(title || "Report", { views: [{ state: "frozen", ySplit: subtitle ? 3 : 2 }] });

  // Title rows
  const titleRow = ws.addRow([`${BRAND.name} — ${title}`]);
  titleRow.font = { bold: true, size: 14, color: { argb: "FF193F7D" } };
  ws.mergeCells(titleRow.number, 1, titleRow.number, columns.length);
  if (subtitle) {
    const subRow = ws.addRow([subtitle]);
    subRow.font = { italic: true, color: { argb: "FF6B6B6B" } };
    ws.mergeCells(subRow.number, 1, subRow.number, columns.length);
  }
  ws.addRow([`Generated ${nowStamp()}`]).font = { italic: true, color: { argb: "FF9E9E9E" }, size: 9 };
  ws.addRow([]);

  // Header row
  const headerRow = ws.addRow(columns.map((c) => c.label));
  headerRow.eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF193F7D" } };
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.border = { bottom: { style: "thin", color: { argb: "FFCCCCCC" } } };
  });

  // Data rows
  rows.forEach((r) => {
    const rowData = columns.map((c) => {
      const v = r[c.key];
      if (c.formatter && c.type !== "currency") return c.formatter(v, r);
      if (v == null) return "";
      if (c.type === "currency") return Number(v || 0);
      return v;
    });
    const row = ws.addRow(rowData);
    columns.forEach((c, i) => {
      const cell = row.getCell(i + 1);
      if (c.type === "currency") {
        cell.numFmt = '"RM"#,##0.00';
        cell.alignment = { horizontal: "right" };
      }
    });
  });

  // Totals row
  if (totals) {
    const totalsRow = ws.addRow(columns.map((c) => {
      const v = totals[c.key];
      if (v == null) return "";
      if (c.type === "currency") return Number(v || 0);
      return v;
    }));
    totalsRow.font = { bold: true };
    totalsRow.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF0F4FA" } };
      cell.border = { top: { style: "medium", color: { argb: "FF193F7D" } } };
    });
    columns.forEach((c, i) => {
      if (c.type === "currency") {
        totalsRow.getCell(i + 1).numFmt = '"RM"#,##0.00';
        totalsRow.getCell(i + 1).alignment = { horizontal: "right" };
      }
    });
  }

  // Column widths
  columns.forEach((c, i) => {
    ws.getColumn(i + 1).width = c.width ? c.width * 0.5 : 20;
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  downloadBlob(blob, `${fileSafe(filename)}.xlsx`);
}

// ---------- Multi-format export bar helper ----------
/**
 * Higher-order helper — page code just calls buildReport() to get the report definition.
 * Then the ExportBar component wires up the three action buttons using this.
 */
export function fireExport(kind, report) {
  if (kind === "pdf") return exportPDF(report);
  if (kind === "csv") return exportCSV(report);
  if (kind === "xlsx") return exportXLSX(report);
}
